// src/context/ReportBuildContext.tsx
//
// Tracks background report builds globally so the customer can navigate away
// the moment a report is created and still be told when it is written
// (plan 2026-08-23 report-creation-background-and-deterministic-templates).
//
// Modelled 1:1 on DwhProvisioningContext — same 5s poll, same
// bootstrap-on-login resume, same pause-while-hidden, same toast + panel
// notification on completion. The one structural difference: provisioning is
// ONE thing per account, a build is one thing per report, so the tracked unit
// is a SET of report public_ids rather than a single status.
//
// The bootstrap poll is what makes a page reload survivable: the drawer's
// `trackBuild` lives in memory only, so after F5 the set is empty and the
// only record of an in-flight build is on the server
// (`GET /reports?build_status=QUEUED,RUNNING`).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "@/routing/accountRouting";
import { notifications } from "@mantine/notifications";
import { useAuth } from "@/context/AuthContext";
import { useNotificationContext } from "@/context/NotificationContext";
import { getReport, listReports, type ReportDTO } from "@/api/reports";
import {
  BUILD_FAILED_TITLE,
  BUILD_READY_TITLE,
  summaryMissingMessage,
  summaryMissingTitle,
  buildFailureMessage,
  isBuildInFlight,
  isSummaryMissing,
} from "@/components/reports/buildStatus";

interface ReportBuildContextType {
  /** Call right after POST /reports answered with a QUEUED/RUNNING build so
   * polling starts immediately instead of at the next bootstrap. */
  trackBuild: (publicId: string) => void;
  /** Report public_ids currently known to be building. */
  buildingReportIds: string[];
  /** Convenience predicate for a single report. */
  isBuilding: (publicId: string) => boolean;
}

const ReportBuildContext = createContext<ReportBuildContextType | undefined>(
  undefined,
);

const POLL_INTERVAL = 5_000;

export function ReportBuildProvider({ children }: { children: ReactNode }) {
  const { user, activeAccount } = useAuth();
  const accountId = activeAccount?.id;
  const isAuthenticated = !!user && !!accountId;

  const navigate = useNavigate();
  const { addNotification } = useNotificationContext();

  const [buildingReportIds, setBuildingReportIds] = useState<string[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // The poll reads the tracked set through a ref: the interval callback is
  // created once per `poll` identity, and a state-only set would leave it
  // polling yesterday's ids.
  const trackedRef = useRef<Set<string>>(new Set());

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const untrack = useCallback((publicId: string) => {
    trackedRef.current.delete(publicId);
    setBuildingReportIds(Array.from(trackedRef.current));
  }, []);

  /** Toast + panel entry for a build that just left QUEUED/RUNNING. */
  const announce = useCallback(
    (report: ReportDTO) => {
      const actionUrl = `/reports/${encodeURIComponent(report.public_id)}`;
      const openReport = () => {
        navigate(actionUrl);
        notifications.cleanQueue();
      };

      if (report.build_status === "READY") {
        const summaryMissing = isSummaryMissing(report);
        // A report built from blocks has readings, not a summary, so the
        // words come from `buildStatus` rather than from a constant chosen
        // here (plan 2026-09-26-one-prose-answer-costs-a-report-all-fourteen-
        // readings).
        const failedTitle = summaryMissingTitle(report);
        const failedMessage = summaryMissingMessage(report);
        notifications.show({
          title: summaryMissing ? failedTitle : BUILD_READY_TITLE,
          message: summaryMissing
            ? failedMessage
            : `"${report.title}" is written and ready to read. Click to open it.`,
          color: summaryMissing ? "yellow" : "green",
          autoClose: 8000,
          onClick: openReport,
          style: { cursor: "pointer" },
        });
        addNotification({
          title: summaryMissing ? failedTitle : BUILD_READY_TITLE,
          message: summaryMissing
            ? failedMessage
            : `"${report.title}" is written and ready to read.`,
          type: summaryMissing ? "warning" : "success",
          category: "reports",
          action_url: actionUrl,
          show_toast: false,
        });
        return;
      }

      if (report.build_status === "FAILED") {
        const message = buildFailureMessage(report.build_error_kind);
        notifications.show({
          title: BUILD_FAILED_TITLE,
          message,
          color: "red",
          autoClose: false,
          onClick: openReport,
          style: { cursor: "pointer" },
        });
        addNotification({
          title: BUILD_FAILED_TITLE,
          message,
          type: "error",
          category: "reports",
          action_url: actionUrl,
          show_toast: false,
        });
      }
      // build_status null → the row was never built through this path (or the
      // report was replaced). Nothing happened the customer needs told.
    },
    [navigate, addNotification],
  );

  const poll = useCallback(async () => {
    const ids = Array.from(trackedRef.current);
    if (ids.length === 0) {
      stopPolling();
      return;
    }
    await Promise.all(
      ids.map(async (publicId) => {
        try {
          const report = await getReport(publicId);
          if (isBuildInFlight(report.build_status)) return;
          // Only announce for an id we were still tracking — two overlapping
          // polls must not fire the toast twice.
          if (!trackedRef.current.has(publicId)) return;
          untrack(publicId);
          announce(report);
        } catch {
          // Silent — a failed poll is non-critical and the next tick retries.
          // A deleted report keeps 404-ing; the tab close ends it.
        }
      }),
    );
    if (trackedRef.current.size === 0) stopPolling();
  }, [stopPolling, untrack, announce]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(poll, POLL_INTERVAL);
  }, [poll]);

  const trackBuild = useCallback(
    (publicId: string) => {
      if (trackedRef.current.has(publicId)) return;
      trackedRef.current.add(publicId);
      setBuildingReportIds(Array.from(trackedRef.current));
      startPolling();
    },
    [startPolling],
  );

  const isBuilding = useCallback(
    (publicId: string) => trackedRef.current.has(publicId),
    [],
  );

  // Which account the tracked set belongs to. Null while logged out.
  const trackedAccountRef = useRef<string | null>(null);

  // On auth/account change: resume tracking whatever the server says is in
  // flight. This is the reload path — `trackBuild` state does not survive F5.
  useEffect(() => {
    if (!isAuthenticated) {
      stopPolling();
      trackedRef.current = new Set();
      setBuildingReportIds([]);
      trackedAccountRef.current = null;
      return;
    }

    // Switching accounts must not keep polling the previous tenant's ids.
    // Deliberately NOT an unconditional wipe: child effects run before the
    // provider's, so a `trackBuild` fired during the same commit (and, in
    // StrictMode, the second run of this very effect) would be erased before
    // the first poll.
    if (
      trackedAccountRef.current !== null &&
      trackedAccountRef.current !== accountId
    ) {
      trackedRef.current = new Set();
      setBuildingReportIds([]);
    }
    trackedAccountRef.current = accountId ?? null;

    let cancelled = false;
    listReports({ build_status: "QUEUED,RUNNING" })
      .then((res) => {
        if (cancelled) return;
        const inFlight = res.reports.filter((r) =>
          isBuildInFlight(r.build_status),
        );
        if (inFlight.length === 0) return;
        for (const r of inFlight) trackedRef.current.add(r.public_id);
        setBuildingReportIds(Array.from(trackedRef.current));
        startPolling();
      })
      .catch(() => {});

    // The cleanup below clears the interval on every re-run of this effect
    // (the deps include `startPolling`, whose identity follows `poll`), so
    // anything already tracked has to have its poll restarted here — waiting
    // for the listReports answer would leave a just-tracked build unpolled if
    // the response raced or came back empty.
    if (trackedRef.current.size > 0) startPolling();

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [isAuthenticated, accountId, startPolling, stopPolling]);

  // Pause polling when the tab is hidden, resume (with an immediate poll)
  // when it comes back — a build that finished while hidden is announced on
  // the first visible tick rather than five seconds later.
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else if (trackedRef.current.size > 0) {
        void poll();
        startPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [isAuthenticated, poll, startPolling, stopPolling]);

  return (
    <ReportBuildContext.Provider
      value={{ trackBuild, buildingReportIds, isBuilding }}
    >
      {children}
    </ReportBuildContext.Provider>
  );
}

export function useReportBuild(): ReportBuildContextType {
  const context = useContext(ReportBuildContext);
  if (!context) {
    throw new Error("useReportBuild must be used within ReportBuildProvider");
  }
  return context;
}

/** Stable no-provider fallback. Module-level so a consumer's useCallback deps
 * don't change identity on every render. */
const NO_BUILD_TRACKING: ReportBuildContextType = {
  trackBuild: () => {},
  buildingReportIds: [],
  isBuilding: () => false,
};

/** Non-throwing variant for surfaces that can legitimately render outside the
 * provider (isolated component tests, storybook). Returns a no-op tracker so
 * the caller never needs a null check. */
export function useReportBuildOptional(): ReportBuildContextType {
  return useContext(ReportBuildContext) ?? NO_BUILD_TRACKING;
}
